import { useEffect, useRef, useState } from "react";
import Seo from "@/components/Seo";
import { RESOURCES } from "@/lib/constants";
import { FileText, ExternalLink, Download } from "lucide-react";

const KANGAROO_IMG =
  "https://customer-assets.emergentagent.com/job_draft-preview-11/artifacts/2g0hgz0l_Aboriginal%20Kangaroo.jpeg";

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <Seo title="Asbestos Safety Resources & Downloads" description="Access trusted asbestos safety factsheets, the ASEA National Strategic Plan, the National Asbestos Exposure Register, the NSW heat map and SafeWork iVerify licence check tools." path="/resources" />

      {/* HERO — bright knowledge-hub style */}
      <section className="bg-gradient-to-br from-[#FFF9D9] via-white to-[#DFF4FF] pt-20 pb-16 relative overflow-hidden">
        <div className="absolute -right-20 top-10 w-72 h-72 rounded-full bg-[#FFB627]/25 blur-3xl" />
        <div className="absolute -left-10 bottom-0 w-60 h-60 rounded-full bg-[#2DB7E8]/20 blur-3xl" />
        <div className="container-x relative z-10 max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-white border border-[#0E2A5C]/15 rounded-full px-3.5 py-1 mb-6 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB627]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E2A5C]">Knowledge Hub</span>
          </div>
          <h1 className="text-[44px] md:text-[64px] font-bold leading-[0.98] text-[#0E2A5C]">
            Trusted asbestos <span className="relative inline-block"><span>guidance</span><span className="absolute inset-x-0 bottom-1 h-3 bg-[#FFB627]/55 -z-10" /></span>,<br /> in one place.
          </h1>
          <p className="mt-6 text-[#0B0F1A]/70 text-[16.5px] max-w-2xl leading-relaxed">
            Safety factsheets, exposure registers, licence verification and emergency procedures from ISGS, SafeWork Australia, ASEA and SafeWork NSW.
          </p>
        </div>
      </section>

      <section className="section-pad bg-vibrant-1">
        <div className="container-x">
          <div className="grid lg:grid-cols-2 gap-5">
            {RESOURCES.map((r, i) => {
              const isPdf = r.type === "PDF";
              const accents = [
                "bg-[#0E2A5C] text-[#A6CD3C]",
                "bg-[#A6CD3C] text-[#0E2A5C]",
                "bg-[#3B6CA8] text-white",
                "bg-[#D89A3D] text-white",
              ];
              return (
                <a
                  key={r.name}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={isPdf ? true : undefined}
                  className="bg-white rounded-2xl p-7 flex items-start gap-5 border border-[#0E2A5C]/10 group transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-22px_rgba(14,42,92,0.3)] hover:border-[#A6CD3C]"
                  data-testid={`resource-${i}`}
                  aria-label={`${r.brand || r.name} — ${isPdf ? "Download" : "Visit"}`}
                >
                  {/* Brand logo (if provided) OR fallback accent icon */}
                  {r.logo ? (
                    <div className="w-28 h-20 flex items-center justify-center shrink-0 overflow-hidden rounded-lg">
                      <img
                        src={r.logo}
                        alt={`${r.brand || r.name} logo`}
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-xl ${accents[i % accents.length]} flex items-center justify-center shrink-0`}>
                      {isPdf ? <FileText size={24} /> : <ExternalLink size={22} />}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#5B6478]">
                        {isPdf ? "PDF · Download" : "External Resource"}
                      </span>
                      {isPdf && r.attached && <span className="text-[10px] text-[#A6CD3C] font-bold">• Attached</span>}
                    </div>
                    <h3 className="text-[17.5px] font-semibold text-[#0E2A5C] leading-snug mb-2">
                      {r.name}
                    </h3>
                    {r.description && (
                      <p className="text-[#5B6478] text-[13.5px] leading-relaxed mb-3">
                        {r.description}
                      </p>
                    )}
                    <div className="text-[#0E2A5C] text-[13px] font-semibold inline-flex items-center gap-1.5 mt-1 group-hover:gap-2.5 transition-all">
                      {isPdf ? <><Download size={14} /> Download</> : <>Visit resource <ExternalLink size={13} /></>}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="mt-16 bg-gradient-to-br from-[#0E2A5C] to-[#0A1F47] rounded-3xl p-10 text-white relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-[#A6CD3C]/15 blur-3xl" />
            <div className="absolute -left-10 -bottom-10 w-60 h-60 rounded-full bg-[#FFB627]/10 blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-[26px] font-bold mb-3">Need a resource that's not listed?</h2>
              <p className="text-white/75 text-[15px] mb-5 max-w-2xl">If you're looking for site-specific guidance, an unexpected-finds procedure or emergency-response documentation, the ISGS team can supply tailored materials on request.</p>
            </div>
          </div>
        </div>
      </section>

      <AcknowledgementBanner />
    </div>
  );
}

/* ─────────── ACKNOWLEDGEMENT OF COUNTRY ─────────── */
function AcknowledgementBanner() {
  const ref = useRef(null);
  const [hopping, setHopping] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHopping(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="acknowledgement-banner"
      aria-label="Acknowledgement of Country"
      className="bg-white py-6 md:py-8 flex justify-center"
    >
      <img
        src={KANGAROO_IMG}
        alt="ISGS Acknowledgement of Country — Aboriginal-style line illustration of a kangaroo with a joey, with dot-art motifs in navy and lime green. Text reads: ISGS acknowledge, respect and value the traditional owners and custodians, past, present and emerging of our nation."
        className={`block w-auto h-[280px] md:h-[360px] lg:h-[420px] max-w-full select-none ${hopping ? "kangaroo-hop-active" : ""}`}
        data-testid="acknowledgement-kangaroo"
        draggable="false"
      />
    </section>
  );
}
